import { createSlice } from '@reduxjs/toolkit';
import {
  fetchMarketOverview,
  fetchTopMovers,
  fetchTopGainers,
  fetchTopLosers,
  fetchMostActive,
  fetchSingleSnapshot,
  fetchBatchSnapshots, // batch thunk
} from './marketSnapshotActions';
import { extractErrorMessage } from '../../../utils/errorMessages';

const initialState = {
  overview: {},           // Market overview data (if needed)
  topMovers: [],
  topGainers: [],
  topLosers: [],
  mostActive: [],
  singleSnapshot: null,   // Data for a single symbol
  singleStatus: 'idle',
  singleError: null,
  batchSnapshots: [],     // Array of MarketSnapshotResponse from batch
  batchStatus: 'idle',
  batchError: null,
  status: 'idle',         // General fetch status for overview/toplists
  error: null,
};

const marketSnapshotSlice = createSlice({
  name: 'marketSnapshot',
  initialState,
  reducers: {
    clearSingleSnapshot: (state) => {
      state.singleSnapshot = null;
      state.singleStatus = 'idle';
      state.singleError = null;
    },
    clearBatchSnapshots: (state) => {
      state.batchSnapshots = [];
      state.batchStatus = 'idle';
      state.batchError = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // === Market Overview/Top Lists ===
      .addCase(fetchMarketOverview.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchMarketOverview.fulfilled, (state, { payload }) => {
        state.status = 'succeeded';
        state.overview = payload;
        state.error = null;
      })
      .addCase(fetchMarketOverview.rejected, (state, { payload, error }) => {
        state.status = 'failed';
        state.error = extractErrorMessage(payload, error.message);
      })
      .addCase(fetchTopMovers.fulfilled, (state, { payload }) => {
        state.topMovers = payload;
      })
      .addCase(fetchTopGainers.fulfilled, (state, { payload }) => {
        state.topGainers = payload;
      })
      .addCase(fetchTopLosers.fulfilled, (state, { payload }) => {
        state.topLosers = payload;
      })
      .addCase(fetchMostActive.fulfilled, (state, { payload }) => {
        state.mostActive = payload;
      })

      // === Single Snapshot ===
      .addCase(fetchSingleSnapshot.pending, (state) => {
        state.singleStatus = 'loading';
        state.singleError = null;
      })
      .addCase(fetchSingleSnapshot.fulfilled, (state, { payload }) => {
        state.singleSnapshot = payload;
        state.singleStatus = 'succeeded';
      })
      .addCase(fetchSingleSnapshot.rejected, (state, { payload, error }) => {
        state.singleStatus = 'failed';
        state.singleError = extractErrorMessage(payload, error.message);
      })

      // === Batch Snapshots ===
      .addCase(fetchBatchSnapshots.pending, (state) => {
        state.batchStatus = 'loading';
        state.batchError = null;
      })
      .addCase(fetchBatchSnapshots.fulfilled, (state, { payload }) => {
        state.batchSnapshots = payload;
        state.batchStatus = 'succeeded';
      })
      .addCase(fetchBatchSnapshots.rejected, (state, { payload, error }) => {
        state.batchStatus = 'failed';
        state.batchError = extractErrorMessage(payload, error.message);
      });
  },
});

export const {
  clearSingleSnapshot,
  clearBatchSnapshots,
  clearError,
} = marketSnapshotSlice.actions;

export default marketSnapshotSlice.reducer;
